import type { StorePlanningShift } from '@gozoki/api-types'
import {
  GRID_TOP,
  minutesOf,
  ROW_COUNT,
  SHIFT_CARD_FOOT,
  SHIFT_CARD_HEAD,
  SHIFT_CARD_LANE_GAP,
  SHIFT_CARD_STACK_GAP,
  SHIFT_CARD_WIDTH,
  TASK_VISIBLE_WIDTH,
  yOf,
} from './planning-constants'

// Emprise d'une carte dans la grille, en px. Elle déborde du créneau par construction (avatar et
// badge au-dessus de l'heure de début, libellé de fin en dessous de l'heure de fin) : on la referme
// sur les bornes affichées plutôt que de la laisser être rognée.
export const shiftCardBounds = (
  shift: Pick<StorePlanningShift, 'startTime' | 'endTime'>,
  rowHeight: number,
) => {
  const gridHeight = GRID_TOP + ROW_COUNT * rowHeight
  const top = Math.max(0, yOf(minutesOf(shift.startTime), rowHeight) - SHIFT_CARD_HEAD)
  const bottom = Math.min(gridHeight, yOf(minutesOf(shift.endTime), rowHeight) + SHIFT_CARD_FOOT)
  return { top, height: bottom - top }
}

export interface ShiftLane {
  lane: number
  laneCount: number
}

// Répartition des cartes en lanes côte à côte, calculée sur leur emprise À L'ÉCRAN et pas sur les
// seules heures : la tête d'une carte monte de 64 px (1 h 20) au-dessus de son début, donc un
// créneau 08:00 rangé dans la même lane qu'un 05:45–07:30 venait recouvrir sa fin. Contrepartie :
// deux créneaux séparés de moins de 1 h 45 passent côte à côte au lieu d'être empilés. Un groupe de
// cartes qui se chevauchent partage la largeur du jour en `laneCount` parts : aucune carte n'en
// recouvre une autre ni ne déborde sur le jour voisin.
export const assignLanes = (
  shifts: Pick<StorePlanningShift, 'id' | 'startTime' | 'endTime'>[],
  rowHeight: number,
): Map<number, ShiftLane> => {
  const layout = new Map<number, ShiftLane>()
  const cards = shifts
    .map((shift) => ({ id: shift.id, ...shiftCardBounds(shift, rowHeight) }))
    .sort((a, b) => a.top - b.top || b.height - a.height)

  let group: { id: number; lane: number }[] = []
  let laneBottoms: number[] = [] // bas (px) de la dernière carte de chaque lane du groupe
  let groupBottom = Number.NEGATIVE_INFINITY
  const closeGroup = () => {
    for (const { id, lane } of group) layout.set(id, { lane, laneCount: laneBottoms.length })
    group = []
    laneBottoms = []
  }

  for (const card of cards) {
    // Plus aucune carte du groupe ne descend jusqu'ici : les suivantes repartent sur toute la largeur.
    if (card.top >= groupBottom + SHIFT_CARD_STACK_GAP) closeGroup()
    const bottom = card.top + card.height
    let lane = laneBottoms.findIndex((laneBottom) => laneBottom + SHIFT_CARD_STACK_GAP <= card.top)
    if (lane === -1) {
      lane = laneBottoms.length
      laneBottoms.push(bottom)
    } else {
      laneBottoms[lane] = bottom
    }
    group.push({ id: card.id, lane })
    groupBottom = Math.max(groupBottom, bottom)
  }
  closeGroup()
  return layout
}

// Largeur Figma tant que le jour le permet, sinon une part égale de la largeur laissée aux cartes.
// Les cartes ne prennent jamais la bande gauche du jour (`TASK_VISIBLE_WIDTH`) : les tâches et
// livraisons, dessinées dessous en pleine largeur, y restent lisibles et cliquables.
export const laneWidth = (laneCount: number): string =>
  `min(${SHIFT_CARD_WIDTH}px, calc((100% - ${TASK_VISIBLE_WIDTH + (laneCount - 1) * SHIFT_CARD_LANE_GAP}px) / ${laneCount}))`

// Lanes rangées depuis le bord droit du jour.
export const laneRight = (lane: number, laneCount: number): string =>
  `calc(${lane} * (${laneWidth(laneCount)} + ${SHIFT_CARD_LANE_GAP}px))`
